import { isNotDefined } from '@sgme/fp';
import { IBlotterPreferenceState } from 'components/shared/Blotter/Grid/blotter.model';
import { parseISO } from 'date-fns';
import {
  BlotterRequest,
  BlotterRequestByBlotterType,
  TradesOrProductsBlotterRequest,
} from 'epic/blotters/tradesBlotter/tradesBlottersEpic';
import { LanguageIsoCode } from 'locales';
import { createSelector } from 'reselect';
import { IBlotterColumnDefinition, Sort } from 'state/blotters/common/blotter.model';
import {
  getSortFromColumnDefinitions,
  toLocaleDate,
  toLocaleDateOnlyString,
} from 'state/blotters/common/blotter.utils';
import { TradesBlotters } from 'state/blotters/trades/tradesBlotters.model';
import { TradesBlottersItemByBlotterType, TradesOrProducts } from 'state/blotters/trades/types/blotterItem';
import {
  AllTradeBlottersFilters,
  AllTradeBlottersFiltersByBlotterType,
  isSpecificTradesOrProductsBlotterFilters,
  SpecificTradesOrProductsBlotterFilters,
  TradeBlottersFiltersByBlotterType,
} from 'state/blotters/trades/types/filters';
import {
  BlotterType,
  FilterBlotterType,
  isMasterDetailBlotterType,
  isNotMasterDetailBlotterType,
  MasterDetailsBlotterType,
  NoMasterDetailsBlotterType,
  PageBlotterType,
  PreferenceBlotterType,
  TradesOrProductsBlotterType,
} from 'state/blotters/trades/types/types';
import { getIsUserInternal } from 'state/selectors';
import { AppState } from 'state/state.model';
import { getTodayStringInLocalTimezone, toEndOfDay, toStartOfDay } from 'utils/core/date';
import { now } from 'utils/core/now';
import { translateText } from 'utils/lang/lang';

const EMPTY_ITEMS: readonly [] = [];

function selectTradesBlottersState<T extends BlotterType>(appState: AppState, blotter: T) {
  return appState.blotters.trades.blotters[blotter];
}

export function selectTradesBlottersColumnDefinition<T extends BlotterType>(appState: AppState, blotter: T) {
  return selectTradesBlottersState(appState, blotter).columnDefinition as readonly IBlotterColumnDefinition<
    TradesBlottersItemByBlotterType<T>
  >[];
}
function selectTradesBlottersSubFilters<T extends FilterBlotterType>(
  appState: AppState,
  blotter: T,
): TradesBlotters[T]['filters'] {
  return selectTradesBlottersState(appState, blotter).filters;
}

export function selectTradesBlottersGlobalFilters(appState: AppState) {
  return selectTradesBlottersGlobalState(appState).filters;
}
function selectTradesBlottersGlobalState(appState: AppState) {
  return appState.blotters.trades;
}

export const selectCommonTradesBlottersQuickSearchFilter = <T extends FilterBlotterType>(s: AppState, blotter: T) =>
  selectTradesBlottersSubFilters(s, blotter).quickSearchTerm;

export const selectCommonTradesBlottersActivityFilter = <T extends FilterBlotterType>(s: AppState, blotter: T) =>
  selectTradesBlottersSubFilters(s, blotter).activityFilter;

export const selectCommonTradesBlottersAdvancedFilter = <T extends FilterBlotterType>(s: AppState, blotter: T) =>
  selectTradesBlottersSubFilters(s, blotter).advancedFilters;

export const selectCommonTradesBlottersDateFilter = createSelector(
  [
    selectCommonTradesBlottersAdvancedFilter,
    <T extends FilterBlotterType>(s: AppState, blotter: T) => selectTradesBlottersSubFilters(s, blotter).dateFilter,
    <T extends FilterBlotterType>(s: AppState, blotter: T) => selectTradesBlottersSubFilters(s, blotter).startDate,
  ],
  (advancedFilters, dateFilter, startDate) => (advancedFilters && startDate ? 'custom' : dateFilter),
);

export const selectCommonTradesBlottersStartDateFilter = createSelector(
  [
    selectCommonTradesBlottersAdvancedFilter,
    <T extends FilterBlotterType>(s: AppState, blotter: T) => selectTradesBlottersSubFilters(s, blotter).dateFilter,
    <T extends FilterBlotterType>(s: AppState, blotter: T) => selectTradesBlottersSubFilters(s, blotter).startDate,
  ],
  (advancedFilters, dateFilter, startDate) =>
    (advancedFilters && startDate !== undefined ? parseISO(startDate) : undefined) ?? toLocaleDate(dateFilter),
);

export const selectCommonTradesBlottersEndDateFilter = createSelector(
  [
    selectCommonTradesBlottersAdvancedFilter,
    <T extends FilterBlotterType>(s: AppState, blotter: T) => selectTradesBlottersSubFilters(s, blotter).endDate,
  ],
  (advancedFilters, endDate) => (advancedFilters && endDate !== undefined ? parseISO(endDate) : undefined) ?? now(),
);

const selectCommonTradesBlottersFilters = createSelector(
  [
    selectCommonTradesBlottersQuickSearchFilter,
    selectCommonTradesBlottersActivityFilter,
    selectCommonTradesBlottersAdvancedFilter,
    selectCommonTradesBlottersDateFilter,
    selectCommonTradesBlottersStartDateFilter,
    selectCommonTradesBlottersEndDateFilter,
    (_state, blotter) => blotter,
  ],
  (quickSearchTerm, activityFilter, advancedFilters, dateFilter, startDate, endDate, blotterType) =>
    ({
      blotterType,
      quickSearchTerm,
      activityFilter,
      advancedFilters,
      dateFilter,
      startDate: toLocaleDateOnlyString(startDate),
      endDate: toLocaleDateOnlyString(endDate),
    }) as Required<AllTradeBlottersFilters>,
);

const selectTradesOrProductsFilters = createSelector(
  [
    <T extends FilterBlotterType>(s: AppState, blotter: T) =>
      selectTradesBlottersSubFilters(s, blotter).advancedFilters,
    <T extends TradesOrProductsBlotterType>(s: AppState, blotter: T) =>
      selectTradesBlottersSubFilters(s, blotter).productFilter,
    <T extends TradesOrProductsBlotterType>(s: AppState, blotter: T) =>
      selectTradesBlottersSubFilters(s, blotter).platformFilter,
    <T extends TradesOrProductsBlotterType>(s: AppState, blotter: T) =>
      selectTradesBlottersSubFilters(s, blotter).currencyFilter,
  ],
  (
    advancedFilters,
    productFilter,
    platformFilter,
    currencyFilter,
  ): Required<SpecificTradesOrProductsBlotterFilters> => {
    return {
      productFilter: productFilter,
      platformFilter: platformFilter || 'allPlatforms',
      currencyFilter: (advancedFilters && currencyFilter) || 'allCurrencies',
    };
  },
);

export const selectAllTradesBlottersFiltersByBlotterType = createSelector(
  [
    <T extends FilterBlotterType>(s: AppState, blotter: T) => selectCommonTradesBlottersFilters(s, blotter),
    <T extends FilterBlotterType>(s: AppState, blotter: T) =>
      blotter === 'trades' || blotter === 'products' ? selectTradesOrProductsFilters(s, blotter) : undefined,
  ],
  (commonFilters, filtersByBlotter): Required<AllTradeBlottersFiltersByBlotterType<FilterBlotterType>> => {
    return {
      ...commonFilters,
      ...(filtersByBlotter ?? {}),
    };
  },
);

export const selectTradesBlottersFiltersByBlotterType = createSelector(
  [
    <T extends FilterBlotterType>(s: AppState, blotter: T) => selectCommonTradesBlottersFilters(s, blotter),
    <T extends FilterBlotterType>(s: AppState, blotter: T) =>
      blotter === 'trades' || blotter === 'products' ? selectTradesOrProductsFilters(s, blotter) : undefined,
  ],
  (
    { blotterType: _, ...commonFilters },
    filtersByBlotter,
  ): Required<TradeBlottersFiltersByBlotterType<FilterBlotterType>> => {
    return {
      ...commonFilters,
      ...(filtersByBlotter ?? {}),
    };
  },
) as <T extends FilterBlotterType>(s: AppState, blotter: T) => Required<TradeBlottersFiltersByBlotterType<T>>;

export function selectTradesBlottersPageItems<T extends BlotterType>(
  appState: AppState,
  blotter: T,
  parentId: T extends MasterDetailsBlotterType ? string : undefined,
) {
  let items;
  if (isNotMasterDetailBlotterType(blotter)) {
    items = selectTradesBlottersState(appState, blotter).items;
  }
  if (isMasterDetailBlotterType(blotter)) {
    items = selectTradesBlottersState(appState, blotter).innerBlotters[parentId as string]?.items;
  }
  return items ?? EMPTY_ITEMS;
}

export function selectTradesBlottersPreferenceState<T extends PreferenceBlotterType>(
  appState: AppState,
  blotter: T,
): IBlotterPreferenceState {
  const state = selectTradesBlottersState(appState, blotter);
  return state.blotterPreferenceState;
}

export function selectTradesBlottersPreferencePageSize<T extends PreferenceBlotterType>(
  appState: AppState,
  blotter: T,
) {
  return selectTradesBlottersPreferenceState(appState, blotter).pageSizeState;
}

export function selectTradesBlottersType(s: AppState) {
  return selectTradesBlottersGlobalState(s).filters.blotterType;
}

export function selectTradesBlottersLoading<T extends BlotterType>(
  appState: AppState,
  blotter: T,
  parentId: T extends MasterDetailsBlotterType ? string : undefined,
) {
  if (isMasterDetailBlotterType(blotter)) {
    return selectTradesBlottersState(appState, blotter).innerBlotters[parentId as string]?.loading;
  }
  return selectTradesBlottersState(appState, blotter as NoMasterDetailsBlotterType).loading;
}

export function selectMasterDetailsErrorContent<T extends MasterDetailsBlotterType>(
  appState: AppState,
  blotter: T,
  parentId: string,
) {
  return selectTradesBlottersState(appState, blotter).innerBlotters[parentId as string]?.errorContent ?? [];
}

export function selectTradesBlottersError<T extends BlotterType>(
  appState: AppState,
  blotter: T,
  parentId: T extends MasterDetailsBlotterType ? string : undefined,
) {
  if (isMasterDetailBlotterType(blotter)) {
    return selectTradesBlottersState(appState, blotter).innerBlotters[parentId as string]?.error;
  }
  return selectTradesBlottersState(appState, blotter as NoMasterDetailsBlotterType).error;
}

export function selectIsEndDateInThePast<T extends FilterBlotterType>(state: AppState, blotter: T): boolean {
  const endDate = selectCommonTradesBlottersEndDateFilter(state, blotter);
  const today = getTodayStringInLocalTimezone();
  return new Date(today) > new Date(endDate);
}

export function getNumberOfKO(appState: AppState): number | undefined {
  const state = selectTradesBlottersState(appState, 'trades');
  return state.creditCheckStatus?.numberOfKO;
}

export function selectTradesBlottersShouldShowCreditCheckBadge(appState: AppState) {
  return getIsUserInternal(appState);
}

export function selectTradesBlottersAreBlotterPreferencesInError<T extends PreferenceBlotterType>(
  state: AppState,
  blotter: T,
): boolean {
  return selectTradesBlottersState(state, blotter).areBlotterPreferencesInError;
}

export function selectTradesBlottersCanSaveBlotterPreferences<T extends PreferenceBlotterType>(
  state: AppState,
  blotter: T,
): boolean {
  return selectTradesBlottersState(state, blotter).canSaveBlotterPreferences;
}

export type BlotterRequestParams = {
  itemsPerPage: number;
  sort?: readonly Sort[];
};

export const selectTradesBlottersRequestParamsByBlotterType = createSelector(
  [
    <T extends PreferenceBlotterType & PageBlotterType>(s: AppState, blotter: T) =>
      selectTradesBlottersState(s, blotter).columnDefinition,
    <T extends PreferenceBlotterType & PageBlotterType>(s: AppState, blotter: T) =>
      selectTradesBlottersState(s, blotter).blotterPreferenceState.pageSizeState,
    <T extends PreferenceBlotterType & PageBlotterType>(s: AppState, blotter: T) =>
      selectTradesBlottersState(s, blotter).sort,
  ],
  (columnDefinition, itemsPerPage, sort): BlotterRequestParams => {
    const computedSort = getSortFromColumnDefinitions(columnDefinition);
    return { itemsPerPage: itemsPerPage ?? 25, sort: sort.length === 0 ? computedSort : sort };
  },
);

export const selectTradesBlotterRequestByBlotterType = createSelector(
  [
    <T extends PreferenceBlotterType & PageBlotterType>(s: AppState, blotter: T) =>
      selectTradesBlottersRequestParamsByBlotterType(s, blotter),
    <T extends PreferenceBlotterType & PageBlotterType>(s: AppState, blotter: T) =>
      selectAllTradesBlottersFiltersByBlotterType(s, blotter),
    <T extends PreferenceBlotterType & PageBlotterType>(s: AppState, _: T) =>
      s.userPreferences?.languageIsoCode ?? 'en',
    <T extends PreferenceBlotterType & PageBlotterType>(_: AppState, blotter: T) => blotter,
  ],
  (params, filter, lang, blotter): BlotterRequestByBlotterType<BlotterType> => {
    return blotter === 'trades' || blotter === 'products'
      ? buildTradesOrProductsRequest(
          params,
          filter as Required<AllTradeBlottersFiltersByBlotterType<typeof blotter>>,
          lang,
        )
      : buildCommonTradesBlottersRequest(params, filter, lang);
  },
) as <T extends BlotterType>(s: AppState, blotter: T) => BlotterRequestByBlotterType<T>;

function buildCommonTradesBlottersRequest<T extends FilterBlotterType>(
  params: BlotterRequestParams,
  filters: Required<AllTradeBlottersFiltersByBlotterType<T>>,
  lang: LanguageIsoCode,
): BlotterRequest {
  const result = {
    size: params.itemsPerPage,
    activityScope: filters.activityFilter,
    sort: params.sort,
    fullTextSearch: translateText(
      lang,
      'en',
      filters.quickSearchTerm.trim().length > 1 ? filters.quickSearchTerm.trim() : '',
    ),
    startDate:
      filters.startDate !== undefined
        ? toStartOfDay(new Date(filters.startDate)).toISOString()
        : toLocaleDateOnlyString(toLocaleDate(filters.dateFilter)),
    endDate: filters.endDate !== undefined ? toEndOfDay(new Date(filters.endDate)).toISOString() : undefined,
  };
  return result;
}
function buildTradesOrProductsRequest(
  params: BlotterRequestParams,
  filters: Required<AllTradeBlottersFiltersByBlotterType<TradesOrProductsBlotterType>>,
  lang: LanguageIsoCode,
): TradesOrProductsBlotterRequest {
  const tradesBlottersRequest = buildCommonTradesBlottersRequest(params, filters, lang);
  return {
    ...tradesBlottersRequest,

    currency: filters.currencyFilter !== 'allCurrencies' ? filters.currencyFilter : undefined,
    product: filters.productFilter !== 'allProducts' ? filters.productFilter : undefined,
    platforms: filters.platformFilter !== 'allPlatforms' ? filters.platformFilter : undefined,
  };
}

export function isTradesBlottersDatesIntervalInThePast<T extends FilterBlotterType>(
  state: AppState,
  blotter: T,
): boolean {
  const filters = selectTradesBlottersFiltersByBlotterType(state, blotter);
  return isEndDateInThePast(filters, blotter);
}

function isEndDateInThePast<T extends FilterBlotterType>(
  filters: Required<TradeBlottersFiltersByBlotterType<T>>,
  blotter: T,
): boolean {
  if (!isSpecificTradesOrProductsBlotterFilters(filters, blotter)) {
    return false;
  }
  if (isNotDefined(filters.endDate)) {
    return false;
  }
  const today = getTodayStringInLocalTimezone();
  return new Date(today) > new Date(filters.endDate);
}

export function getTradesBlotterAvailablePlatforms(state: AppState) {
  return state.blotters.trades.availablePlatforms;
}

export function getTradesBlottersAvailablePlatformsFetchingStatus(state: AppState) {
  return state.blotters.trades.availablePlatformsFetchingStatus;
}

export const getTradesBlotterRequestByBlotterType = createSelector(
  [
    (s: AppState, t: TradesOrProducts) => selectTradesBlottersState(s, t).columnDefinition,
    (s: AppState, t: TradesOrProducts) => selectTradesBlottersState(s, t).sort,
    (s: AppState, t: TradesOrProducts) => selectTradesBlottersState(s, t).blotterPreferenceState.pageSizeState,
    (s: AppState, t: TradesOrProducts) => selectTradesBlottersFiltersByBlotterType(s, t),
    (_s: AppState, _: TradesOrProducts, lang: LanguageIsoCode) => lang,
  ],
  (columnDefinition, sort, pageSize, filters, lang): TradesOrProductsBlotterRequest => {
    const computedSort = getSortFromColumnDefinitions(columnDefinition);
    const tradesBlotterRequest: TradesOrProductsBlotterRequest = {
      size: pageSize ?? 25,
      sort: sort ?? computedSort,
      startDate: toStartOfDay(new Date(filters.startDate)).toISOString(),
      endDate: toEndOfDay(new Date(filters.endDate)).toISOString(),
      activityScope: filters.activityFilter,
    };
    if (filters.quickSearchTerm) {
      tradesBlotterRequest.fullTextSearch = translateText(
        lang,
        'en',
        filters.quickSearchTerm.trim().length > 1 ? filters.quickSearchTerm.trim() : '',
      );
    }
    if (filters.advancedFilters && filters.currencyFilter !== 'allCurrencies') {
      tradesBlotterRequest.currency = filters.currencyFilter;
    }

    if (filters.productFilter && filters.productFilter !== 'allProducts') {
      tradesBlotterRequest.product = filters.productFilter;
    }

    if (filters.platformFilter && filters.platformFilter !== 'allPlatforms') {
      tradesBlotterRequest.platforms = filters.platformFilter;
    }

    return tradesBlotterRequest;
  },
);

export function getTradesBlotterPreferenceState(state: AppState, blotter: 'trades' | 'products') {
  return state.blotters.trades.blotters[blotter].blotterPreferenceState;
}
